/* eslint-disable max-lines-per-function */
import type {
  KLineData,
  ExtendLineSeries,
  ExtendBarSeries,
  ExtendTagSeries
} from '../helper/hxKLine';

type VisibleDataItem = {
  data: KLineData;
  dataIndex: number;
  x: number;
};

function truncateText(
  str: string,
  containerWidth: number,
  fontSize: number,
  measureText: (text: string, size: number) => number
) {
  if (containerWidth <= 0 || str.length === 0) {
    return '';
  }

  // 测量基础数据
  const ellipsis = '...';
  const ellipsisWidth = measureText(ellipsis, fontSize);
  const fullWidth = measureText(str, fontSize);

  // 完整显示检查
  if (fullWidth <= containerWidth) {
    return str;
  }

  // 检查是否只能放下一个字符
  const firstCharWidth = measureText(str.substring(0, 1), fontSize);
  if (firstCharWidth <= containerWidth && firstCharWidth + ellipsisWidth > containerWidth) {
    return str.substring(0, 1);
  }

  // 二分查找最佳截断位置
  let low = 1;
  let high = str.length - 1;
  let best = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const currentStr = str.substring(0, mid);
    const currentWidth = measureText(currentStr, fontSize);

    if (currentWidth + ellipsisWidth <= containerWidth) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best === -1 ? '' : str.substring(0, best) + ellipsis;
}

const getStateStyles = ({ isActive, isHover }, styles) => {
  const {
    activeBackground,
    hoverBackground,
    defaultBackground,
    activeBorder,
    hoverBorder,
    defaultBorder
  } = styles;
  const state = {
    background: defaultBackground,
    border: defaultBorder
  };
  if (isActive) {
    state.background = activeBackground;
    state.border = activeBorder;
  } else if (isHover) {
    state.background = hoverBackground;
    state.border = hoverBorder;
  }
  return state;
};

const getCandleStatus = (close, open, pre) => {
  if (close !== open) {
    return close > open ? 'up' : 'down';
  }
  if (close !== pre) {
    return close > pre ? 'up' : 'down';
  }
  return 'neutral';
};

function processExtendData({
  extendData,
  dataList,
  xAxis,
  containerHeight,
  halfGapBar,
  figures,
  klineDataListForPaint
}) {
  const titleContainerMargin = 2;
  const titleContainerPadding = 4;

  extendData.forEach(item => {
    const { beginTimestamp, endTimestamp, titleList, textSize } = item;
    const styleConfig = {
      activeBackground: item.activeBackgroundColor,
      hoverBackground: item.hoverBackgroundColor,
      defaultBackground: item.backgroundColor,
      activeBorder: item.activeBorderColor,
      hoverBorder: item.hoverBorderColor,
      defaultBorder: item.borderColor
    };
    const beginIndex = dataList.findIndex(d => d.timestamp === beginTimestamp);
    let endIndex = dataList.findIndex(d => d.timestamp === endTimestamp);
    const { background, border } = getStateStyles(item, styleConfig);

    // 如果只有起始时间找到但结束时间未找到，则认为结束时间为最后一个数据项
    if (beginIndex !== -1 && endIndex === -1) {
      endIndex = dataList.length - 1;
    }
    // 如果两个索引均无效，则跳过该区域
    if (beginIndex === -1 && endIndex === -1) {
      return;
    }

    const startX = xAxis.convertToPixel(beginIndex) - halfGapBar;
    const endX = xAxis.convertToPixel(endIndex) + halfGapBar;

    // 将该区域内的 kline 数据存入绘制数组中
    klineDataListForPaint.push(...dataList.slice(beginIndex, endIndex + 1));

    // 添加覆盖区域的背景矩形
    figures.push({
      key: item.id,
      type: 'rect',
      attrs: {
        x: startX,
        y: 0,
        width: endX - startX,
        height: containerHeight
      },
      styles: {
        style: 'stroke_fill',
        color: background,
        borderColor: border
      }
    });

    // 计算标题区域高度：文本高度 + 内边距
    const titleHeight = textSize + titleContainerPadding * 2;
    titleList.forEach((concept, idx) => {
      // 标题 Y 坐标计算：容器高度 - (标题高度+间距)*序号
      const titleY = containerHeight - (titleHeight + titleContainerMargin) * (idx + 1);
      // 计算标题容器的宽度，至少为 0
      const titleContainerWidth = Math.max(endX - startX - titleContainerMargin * 2, 0);

      // 添加标题背景矩形
      figures.push({
        key: `${item.id}titleRect${idx}`,
        type: 'rect',
        attrs: {
          x: startX + titleContainerMargin,
          y: titleY,
          width: titleContainerWidth,
          height: titleHeight
        },
        styles: concept.rect.styles,
        ignoreEvent: true
      });

      // 限制文本宽度（扣除容器左右 margin 和 padding）
      const textWidthLimit =
        titleContainerWidth - titleContainerMargin * 2 - titleContainerPadding * 2;
      const text = truncateText(
        concept.text.name,
        textWidthLimit,
        textSize,
        HXKlineChart.utils.calcTextWidth
      );
      // 添加标题文本
      figures.push({
        key: `${item.id}text${idx}`,
        type: 'text',
        attrs: {
          x: startX + titleContainerMargin + titleContainerPadding,
          y: titleY - titleContainerPadding * 2 + textSize / 2,
          text
        },
        styles: concept.text.styles,
        ignoreEvent: true
      });
    });
  });
}

function processCandles({
  klineDataList,
  xAxis,
  yAxis,
  halfGapBar,
  gapBar,
  defaultStyles,
  figures
}) {
  const {
    upColor,
    upBorderColor,
    upWickColor,
    downColor,
    downBorderColor,
    downWickColor,
    noChangeColor,
    noChangeBorderColor,
    noChangeWickColor
  } = defaultStyles.candle.bar;

  klineDataList.forEach(item => {
    const { open, high, low, close, index, pre, timestamp } = item;
    const x = xAxis.convertToPixel(index);
    const openY = yAxis.convertToPixel(open);
    const highY = yAxis.convertToPixel(high);
    const lowY = yAxis.convertToPixel(low);
    const closeY = yAxis.convertToPixel(close);

    // 根据开盘和收盘价格（或与前一价格比较）确定颜色
    const COLOR_CONFIG = {
      up: {
        body: upColor,
        border: upBorderColor,
        wick: upWickColor
      },
      down: {
        body: downColor,
        border: downBorderColor,
        wick: downWickColor
      },
      neutral: {
        body: noChangeColor,
        border: noChangeBorderColor,
        wick: noChangeWickColor
      }
    };
    // 3. 获取颜色配置
    const {
      body: bodyColor,
      border: borderColor,
      wick: wickColor
    } = COLOR_CONFIG[getCandleStatus(close, open, pre)];

    // 添加蜡烛矩形（实体）
    figures.push({
      key: `${timestamp}rect`,
      type: 'rect',
      attrs: {
        x: x - halfGapBar,
        y: Math.min(openY, closeY),
        width: gapBar,
        height: Math.abs(closeY - openY) || 1
      },
      styles: {
        color: bodyColor,
        borderColor
      }
    });

    // 添加蜡烛线（影线）
    figures.push({
      key: `${timestamp}line`,
      type: 'line',
      attrs: {
        coordinates: [
          { x, y: highY },
          { x, y: lowY }
        ]
      },
      styles: {
        color: wickColor
      }
    });
  });
}

if (window.HXKlineChart) {
  /**
   * 扩展一个折线系列
   */
  // 注册折线图覆盖物
  HXKlineChart.registerOverlay({
    name: 'line',
    lock: true,
    // @ts-ignore
    createPointFigures: config => {
      const { overlay, coordinates, dataList, bounding, barSpace } = config;
      const extendData = overlay.extendData as ExtendLineSeries['extendData'];
      const firstInvalidKLinePointIndex = dataList.findIndex(item => {
        return Number.isNaN(+item.close);
      });
      const isFull = firstInvalidKLinePointIndex === -1;
      const lastKLinePoint = isFull
        ? extendData.data.at(-1)
        : dataList[Math.max(firstInvalidKLinePointIndex - 1, 0)];
      // 找出最后一个非NaN的点，作为折线的终点
      let lastedPointIndex = extendData.data.findIndex(item => {
        return item.timestamp >= lastKLinePoint.timestamp;
      });
      // 如果找不到，说明最后一个点在数据列表的最后，取最后一个点
      lastedPointIndex = lastedPointIndex === -1 ? extendData.data.length - 1 : lastedPointIndex;

      const lingPlayDataList = coordinates
        .map((item, index) => ({ ...item, dataIndex: extendData.data[index].dataIndex }))
        .slice(0, isFull ? lastedPointIndex + 1 : lastedPointIndex);

      if (lastedPointIndex > -1 && lingPlayDataList.length >= 2) {
        // * 利用占位柱图来触发 tooltip，解决折线图无法 tooltip 交互问题
        const rects: unknown[] = [];
        if (extendData.enableTooltip) {
          lingPlayDataList.forEach(item => {
            const x1 = item.x;
            const width = barSpace.bar > 5 ? barSpace.bar : 5;
            rects.push({
              key: item.dataIndex,
              type: 'rect',
              attrs: {
                x: x1 - width * 0.4,
                y: 0,
                width: width * 0.8,
                height: bounding.height
              },
              styles: {
                color: 'rgba(255, 0, 0, 0.0)'
              }
            });
          });
        }

        return [
          {
            type: 'line',
            attrs: {
              coordinates: lingPlayDataList
            },
            styles: {
              color: extendData?.color ?? 'rgba(255, 0, 0, 0.5)',
              size: extendData?.size ?? 2
            },
            ignoreEvent: true
          },
          ...rects
        ];
      }

      return [];
    },
    // @ts-ignore
    onMouseEnter: event => {
      (event.overlay.extendData as ExtendLineSeries['extendData'])?.onMouseEnter?.({
        x: event.x!,
        y: event.y!,
        dataIndex: event.figureKey as unknown as number
      });
    },
    // @ts-ignore
    onMouseLeave: event => {
      (event.overlay.extendData as ExtendLineSeries['extendData'])?.onMouseLeave?.({
        x: event.x!,
        y: event.y!,
        dataIndex: event.figureKey as unknown as number
      });
    }
  });

  /**
   * 扩展一个柱状系列
   */
  // 注册柱状图覆盖物
  HXKlineChart.registerOverlay({
    name: 'bar',
    lock: true,
    createPointFigures: params => {
      const { overlay, bounding, yAxis, barSpace } = params;
      const { barData, gap, color, highlightColor, highlightIndex } =
        overlay.extendData as ExtendBarSeries['extendData'];
      const extendData = overlay.extendData as ExtendBarSeries['extendData'];
      const visibleDataList = extendData.hxKLine.getVisibleDataList() as VisibleDataItem[];
      const visibleStart = visibleDataList[0].data;
      const visibleEnd = visibleDataList.at(-1).data;
      const overlayDataVisible = barData.filter((item, i) => {
        return item.timestamp >= visibleStart.timestamp && item.timestamp <= visibleEnd.timestamp;
      });

      // 计算绘图点数据的最大值和最小值，以实现等比例尺
      if (overlayDataVisible.length > 0) {
        let overlayDataVisibleMax = overlayDataVisible[0].value;
        const overlayDataVisibleMin = 0;
        overlayDataVisible.forEach(element => {
          if (element.value > overlayDataVisibleMax) {
            overlayDataVisibleMax = element.value;
          }
        });
        let overlayDataVisibleRange = overlayDataVisibleMax - overlayDataVisibleMin;
        extendData?.onUpdateLeftYAxis?.({
          min: overlayDataVisibleMin,
          max: overlayDataVisibleMax
        });

        // 扩大绘图点数据的范围，以实现柱子不贴上边
        overlayDataVisibleMax += overlayDataVisibleRange * gap;
        overlayDataVisibleRange = overlayDataVisibleMax - overlayDataVisibleMin;
        const { min, range } = yAxis._extremum;
        const suitableYAxis = (value: number) => {
          const timeSize = range / overlayDataVisibleRange;
          const yValue = (value - overlayDataVisibleMin) * timeSize;
          return (min + yValue) as number;
        };

        // 绘图
        const drawBarDataList = [];
        overlayDataVisible.forEach(item => {
          const x1 = extendData.hxKLine.convertToPixel(
            { timestamp: item.timestamp },
            { paneId: 'candle_pane' }
          ).x as number;
          const width = barSpace.bar > 5 ? barSpace.bar : 5;
          const y1 = yAxis.convertToPixel(suitableYAxis(item.value));
          const isHighlight = item.dataIndex === highlightIndex;
          drawBarDataList.push({
            key: item.dataIndex,
            type: 'rect',
            attrs: {
              x: x1 - width * 0.4,
              y: y1,
              width: width * 0.8,
              height: bounding.height - y1
            },
            styles: {
              color: isHighlight ? highlightColor : color
            }
          });
        });

        return drawBarDataList;
      }
      return [];
    },
    // @ts-ignore
    onMouseEnter: event => {
      (event.overlay.extendData as ExtendBarSeries['extendData'])?.onMouseEnter?.({
        x: event.x!,
        y: event.y!,
        dataIndex: event.figureKey as unknown as number
      });

      (event.overlay.extendData as ExtendBarSeries['extendData']).highlightIndex = Number(
        event.figureKey
      );
    },
    // @ts-ignore
    onMouseLeave: event => {
      (event.overlay.extendData as ExtendBarSeries['extendData'])?.onMouseLeave?.({
        x: event.x!,
        y: event.y!,
        dataIndex: event.figureKey as unknown as number
      });

      (event.overlay.extendData as ExtendBarSeries['extendData']).highlightIndex = undefined;
    }
  });

  /**
   * 扩展一个标签系列
   */
  // 注册标签覆盖物
  HXKlineChart.registerOverlay({
    name: 'tag',
    lock: true,
    // 创建点对应的图形信息：矩形背景，矩形文本背景、单行文本
    createPointFigures: params => {
      const { overlay, bounding, barSpace, xAxis, yAxis, dataList, defaultStyles } = params;
      dataList.forEach((item, index) => {
        item.index = index;
      });
      const { extendData }: { extendData: ExtendTagSeries['extendData'] } = overlay;
      const containerHeight = bounding.height;
      const { halfGapBar, gapBar } = barSpace;
      const klineDataListForPaint = [];
      const res = [];
      if (extendData?.length > 0) {
        processExtendData({
          extendData,
          dataList,
          xAxis,
          containerHeight,
          halfGapBar,
          figures: res,
          klineDataListForPaint
        });
      }
      processCandles({
        klineDataList: klineDataListForPaint,
        xAxis,
        yAxis,
        halfGapBar,
        gapBar,
        defaultStyles,
        figures: res
      });
      return res;
    },
    // 点击回调事件
    onClick: event => {
      (event.overlay.extendData as ExtendTagSeries['extendData']).forEach(item => {
        item.isActive = false;
      });
      const target = (event.overlay.extendData as ExtendTagSeries['extendData'])?.find(
        item => item.id === event.figureKey
      );
      if (typeof target === 'undefined') {
        return;
      }
      target.isActive = true;
      target?.onClick?.({
        id: event.figureKey
      });
    },
    // 鼠标移入事件
    onMouseEnter: event => {
      const target = (event.overlay.extendData as ExtendTagSeries['extendData'])?.find(
        item => item.id === event.figureKey
      );
      if (typeof target === 'undefined') {
        return;
      }
      target.isHover = true;
      target?.onMouseEnter?.({
        id: event.figureKey
      });
    },
    // 鼠标移出事件
    onMouseLeave: event => {
      const target = (event.overlay.extendData as ExtendTagSeries['extendData'])?.find(
        item => item.id === event.figureKey
      );
      if (typeof target === 'undefined') {
        return;
      }
      target.isHover = false;
      target?.onMouseLeave?.({
        id: event.figureKey
      });
    }
  });
}
/**
 * 根据 k 线数据集调整给定数据集的时间戳
 *
 * ! 因为在行情图上叠加其它图形，必须要求其它图形的数据点和 k 线的数据点是同一时间戳
 */
export function adaptData<D extends { timestamp: number }>(
  klineData: KLineData[],
  targetData: D[]
) {
  const times = klineData.map(item => item.timestamp);
  const result = [] as { timestamp: number; __originData: D }[];

  targetData.forEach(item => {
    let index = times.findLastIndex(timestamp => item.timestamp >= timestamp);

    if (index === -1) {
      index = item.timestamp < times[0] ? 0 : times.length - 1;
    }

    result.push({
      timestamp: times[index],
      __originData: item
    });
  });

  return result;
}

/**
 * 根据给定时间戳寻找 k 线数据索引
 */
export function findIndexByTimestamp(
  klineData: KLineData[],
  timestamp: number,
  find: 'start' | 'end'
) {
  const timestampTemp = Math.min(
    Math.max(timestamp, klineData[0].timestamp),
    klineData[klineData.length - 1].timestamp
  );

  return find === 'start'
    ? klineData.findIndex(item => item.timestamp >= timestampTemp)
    : klineData.findLastIndex(item => item.timestamp <= timestampTemp);
}
